import React, { useEffect, useState } from "react";
import styles from "./RepositionOrderDashboard.module.scss";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Space,
  Spin,
  Button,
  Select,
  Typography
} from "antd";
import { 
  WarningOutlined,
  DollarOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  PieChartOutlined,
  AlertOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { getAllBranchOffices } from "../../../api/Services";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend,
  Tooltip
);

interface BranchOffice {
  id: number;
  name: string;
}

const RepositionOrderDashboard = () => {
  const [selectedBranchOffice, setSelectedBranchOffice] = useState<number | null>(null);
  const [branchOffices, setBranchOffices] = useState<BranchOffice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingBranchOffices, setIsLoadingBranchOffices] = useState<boolean>(true);

  useEffect(() => {
    const fetchBranchOffices = async () => {
      setIsLoadingBranchOffices(true);
      try {
        const offices = await getAllBranchOffices();
        setBranchOffices(offices);
        if (offices.length > 0) {
          setSelectedBranchOffice(offices[0].id);
        }
      } catch (error) {
        console.error("Error fetching branch offices:", error);
      } finally {
        setIsLoadingBranchOffices(false);
      }
    };
    fetchBranchOffices();
  }, []);

  const handleUpdate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const surtidoAlerts = [
    {
      id: 1,
      type: "stock_break",
      title: "Quiebre de stock inminente en 3 productos",
      severity: "error"
    },
    {
      id: 2,
      type: "promotion",
      title: "Promoción próxima a finalizar en 2 días",
      severity: "warning"
    },
    {
      id: 3,
      type: "season",
      title: "Fin de temporada próximo - 5 productos afectados",
      severity: "warning"
    },
    {
      id: 4,
      type: "overstock",
      title: "Sobrestock detectado en categoría Deportivos",
      severity: "warning"
    }
  ];

  const topProductsData = {
    labels: ["Producto A", "Producto B", "Producto C", "Producto D", "Producto E"],
    datasets: [
      {
        label: "Unidades vendidas",
        data: [450, 380, 320, 280, 250],
        backgroundColor: "#1890ff",
        borderRadius: 4,
      },
    ],
  };

  const rotationByCategoryBrandData = {
    labels: [
      "Deportivos Skechers",
      "Casuales Nike",
      "Formales Adidas",
      "Deportivos Puma",
      "Casuales Reebok",
      "Formales Converse",
      "Deportivos New Balance",
      "Casuales Vans",
      "Formales Timberland",
      "Deportivos Under Armour"
    ],
    datasets: [
      {
        label: "% de venta",
        data: [25, 18, 15, 12, 10, 8, 5, 4, 2, 1],
        backgroundColor: [
          "#8b5cf6",
          "#1890ff",
          "#f5222d",
          "#fa8c16",
          "#52c41a",
          "#13c2c2",
          "#722ed1",
          "#eb2f96",
          "#faad14",
          "#a0d911"
        ],
        borderRadius: 4,
      },
    ],
  };

  const repositionStatusData = {
    labels: ["Borrador", "Enviado", "En Tránsito", "Completada"],
    datasets: [
      {
        data: [5, 12, 8, 25],
        backgroundColor: ["#f5222d", "#8b5cf6", "#1890ff", "#52c41a"],
        borderWidth: 0,
      },
    ],
  };

  const kpis = {
    productosQuiebreProximo: 12,
    productosSobrestock: 8,
    optimizacionCatalogo: 0,
    optimizacionInventario: 0,
    rotacion: 0,
    inventarioValorizado: 1250000
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      x: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
      y: {
        grid: {
          display: false
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
    },
  };

  return (
    <div className={styles.dashboardWrapper}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerContent}>
          <Space size="middle">
            <Select
              className={styles.branchSelector}
              value={selectedBranchOffice ?? undefined}
              onChange={setSelectedBranchOffice}
              loading={isLoadingBranchOffices}
              placeholder="Seleccionar sucursal"
              style={{ minWidth: 200 }}
            >
              {branchOffices.map((office) => (
                <Select.Option key={office.id} value={office.id}>
                  {office.name}
                </Select.Option>
              ))}
            </Select>
            <Button type="primary" onClick={handleUpdate} className={styles.updateButton}>
              Actualizar
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={18}>
          <div className={styles.kpisSection}>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Productos con quiebre próximo"
                      value={formatNumber(kpis.productosQuiebreProximo)}
                      prefix={<WarningOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Productos con sobrestock"
                      value={formatNumber(kpis.productosSobrestock)}
                      prefix={<ExclamationCircleOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Optimización de catálogo"
                      value={formatNumber(kpis.optimizacionCatalogo)}
                      prefix={<BarChartOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Optimización de inventario"
                      value={formatNumber(kpis.optimizacionInventario)}
                      prefix={<PieChartOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Rotación"
                      value={formatNumber(kpis.rotacion)}
                      prefix={<ShoppingOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Inventario valorizado"
                      value={kpis.inventarioValorizado}
                      prefix={<DollarOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#13c2c2', fontSize: '12px' }}
                      formatter={(value) => formatCurrency(Number(value))}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
          </div>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Top productos por demanda</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={topProductsData} options={chartOptions} />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <PieChartOutlined />
                    <span>Estado de reposiciones</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Doughnut data={repositionStatusData} options={doughnutOptions} />
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Rotación de inventario por categoría y marca</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={rotationByCategoryBrandData} options={horizontalBarOptions} />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={6}>
          <Card 
            title={
              <Space>
                <AlertOutlined />
                <span>Alertas de surtido</span>
              </Space>
            }
            className={styles.alertsCard}
          >
            <div className={styles.alertsList}>
              {surtidoAlerts.map((alert) => (
                <div key={alert.id} className={styles.alertItem}>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertText}>{alert.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RepositionOrderDashboard;